/**
 * minimap-math.ts — Plan 05-04 Task 1.
 *
 * Pure math helpers for the 2D SVG minimap (UI-03). Zero imports from
 * three / react / zustand — consumers (Minimap.tsx) bring the runtime values
 * via Zustand selectors; this module is 100% unit-testable in plain node.
 *
 * ================================================================
 * Z-CONVENTION (Plan 05-04 Task 1 decision, documented here for future readers)
 * ================================================================
 *
 * 05-RESEARCH.md Pattern 5's sample `toLocal` formula negates Z:
 *
 *     y = -(wz - pos[2]) * M_TO_PX + MAP_SIZE_PX / 2    // research-doc
 *
 * That formula assumes +Z = north in world space. **Edgewood Lake uses
 * Three.js's default right-handed coordinate system, where the default camera
 * looks down the NEGATIVE Z axis.** In that convention, +Z points TOWARD the
 * viewer (i.e., "south" if the camera's initial heading is north). Concretely:
 * a target 50m north of the player is at `wz = -50`, not `wz = +50`.
 *
 * "North-up" on the minimap means NORTH appears at the TOP of the SVG (small
 * Y in SVG coordinates, where +Y is DOWN). So for a north-ahead target
 * (wz = -50, pos[2] = 0) we want the SVG localY = 0 (top edge).
 *
 * The formula below achieves this WITHOUT negating Z:
 *
 *     localY = (wz - pos[2]) * M_TO_PX + MAP_SIZE_PX / 2    // Three.js convention
 *
 * At wz=-50, pos[2]=0 → localY = -50 * 1.2 + 60 = 0 (top edge). Correct.
 * At wz=+50, pos[2]=0 → localY = +50 * 1.2 + 60 = 120 (bottom edge). Correct.
 *
 * This is a deviation from the research-doc sample but correct for the
 * Three.js coordinate system the rest of the codebase uses (Canvas default
 * camera, StreetcarRig waypoints at -Z going "away into the mountains", etc.).
 *
 * See StreetcarRig.tsx waypoint 1 at [0, 8, -50] ("climb south" in physical
 * Birmingham geography means -Z in our stylized local coords — i.e., the
 * route runs toward NEGATIVE Z as the streetcar leaves the Five Points South
 * platform heading south over Red Mountain). If we ever migrate to a different
 * convention, this header block is the anchor for the audit.
 * ================================================================
 */

/** Minimap shows a 50m radius around the player — 100m diameter window. */
export const MAP_RADIUS_M = 50;

/** SVG size in pixels — 120x120 is the sweet spot for corner HUD visibility. */
export const MAP_SIZE_PX = 120;

/**
 * Meters-to-pixels scale factor.
 *
 * Derivation: the 2 * MAP_RADIUS_M meters of world space need to fit inside
 * MAP_SIZE_PX pixels of SVG space.
 */
export const M_TO_PX = MAP_SIZE_PX / (2 * MAP_RADIUS_M); // = 1.2

/**
 * Extract camera yaw (Y-axis rotation) from a quaternion `[x, y, z, w]`.
 *
 * Standard atan2 form for extracting yaw from a unit quaternion. The formula
 * gives the angle in radians about the world Y axis; +yaw rotates the camera
 * to the left in world coordinates (right-hand rule, Y up).
 *
 * Matches the formula in 05-RESEARCH.md Pattern 5:
 *
 *     yaw = atan2(2 * (w*y + x*z), 1 - 2 * (y*y + z*z))
 */
export function quaternionToYaw(q: [number, number, number, number]): number {
  const [x, y, z, w] = q;
  return Math.atan2(2 * (w * y + x * z), 1 - 2 * (y * y + z * z));
}

/**
 * Project a world-space (X, Z) point into minimap-local SVG coordinates.
 *
 * @param wx          World-space X of the target
 * @param wz          World-space Z of the target (Three.js convention: NORTH = -Z)
 * @param playerPos   World-space [x, y, z] of the player (Y ignored)
 * @param size        SVG size in pixels (typically MAP_SIZE_PX = 120)
 * @returns `[localX, localY]` in SVG pixel coordinates (origin = top-left)
 *
 * See header block for Z-convention rationale.
 */
export function worldToLocal(
  wx: number,
  wz: number,
  playerPos: [number, number, number],
  size: number,
): [number, number] {
  const localX = (wx - playerPos[0]) * M_TO_PX + size / 2;
  const localY = (wz - playerPos[2]) * M_TO_PX + size / 2;
  return [localX, localY];
}

/**
 * Check whether a local SVG coordinate is within the visible map window.
 *
 * Inclusive on both edges: a POI exactly at (0, size/2) is considered in-view
 * because that's the left edge of the circular map background.
 */
export function isInView(localX: number, localY: number, size: number): boolean {
  return localX >= 0 && localX <= size && localY >= 0 && localY <= size;
}
